import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface PartnerResource {
  id: string;
  title: string;
  description?: string;
  file_url: string;
  file_type?: string;
  file_size?: number;
  category: string;
  uploaded_by_staff_id?: string;
  visible_to_partner_ids: string[];
  is_public_to_all_partners: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateResourceData {
  title: string;
  description?: string;
  file_url: string;
  file_type?: string;
  file_size?: number;
  category?: string;
  visible_to_partner_ids?: string[];
  is_public_to_all_partners?: boolean;
}

export const usePartnerResources = (partnerId?: string) => {
  return useQuery({
    queryKey: ["partner-resources", partnerId],
    queryFn: async () => {
      let query = supabase
        .from('partner_resources')
        .select('*');

      // Filter resources for partners
      if (partnerId) {
        query = query.or(`is_public_to_all_partners.eq.true,visible_to_partner_ids.cs.{${partnerId}}`);
      }

      const { data, error } = await query.order('created_at', { ascending: false });
      
      if (error) {
        console.error("Error fetching partner resources:", error);
        throw error;
      }
      return data as PartnerResource[];
    }
  });
};

export const useCreatePartnerResource = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: CreateResourceData) => {
      const { data: result, error } = await supabase
        .from('partner_resources')
        .insert(data)
        .select()
        .single();
      
      if (error) {
        throw new Error(error.message || "Failed to create resource");
      }
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["partner-resources"] });
      toast({
        title: "Success",
        description: "Resource uploaded successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: `Failed to upload resource: ${error.message}`,
        variant: "destructive",
      });
    }
  });
};

export const useUpdatePartnerResource = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<CreateResourceData> }) => {
      const { data: result, error } = await supabase
        .from('partner_resources')
        .update(data)
        .eq('id', id)
        .select()
        .single();
      
      if (error) {
        throw new Error(error.message || "Failed to update resource");
      }
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["partner-resources"] });
      toast({
        title: "Success",
        description: "Resource updated successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: `Failed to update resource: ${error.message}`,
        variant: "destructive",
      });
    }
  });
};

export const useDeletePartnerResource = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('partner_resources')
        .delete()
        .eq('id', id);
      
      if (error) {
        throw new Error(error.message || "Failed to delete resource");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["partner-resources"] });
      toast({
        title: "Success",
        description: "Resource deleted successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: `Failed to delete resource: ${error.message}`,
        variant: "destructive",
      });
    }
  });
};